import {useState, useMemo} from 'react';
import {Form, Input, Switch, InputNumber, Select} from 'antd';
import {useMutation} from '@apollo/client';
import {UPDATE_MINI_FEEDBACK} from './resolvers';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import styles from './CreateMiniFeedback.module.scss';

const FEATURE_IDS = [
  {value: 'TRADE', label: 'Trade'},
  {value: 'DABA_PRO', label: 'Daba Pro'},
  {value: 'ANALYTICS', label: 'Analytics'},
  {value: 'WALLET', label: 'Wallet'},
  {value: 'INVESTMENT_PORTFOLIO', label: 'Investment Portfolio'},
  {value: 'SAVINGS', label: 'Savings'},
  {value: 'ACADEMY', label: 'Academy'},
  {value: 'NEWS', label: 'News'},
  {value: 'SIMULATOR', label: 'Simulator'},
  {value: 'GROUPS', label: 'Groups'},
  {value: 'QUICK_ACTIONS', label: 'Quick Actions'},
  {value: 'LEADERBOARD', label: 'Leaderboard'},
  {value: 'SIMULATOR_LEADERBOARD', label: 'Simulator Leaderboard'},
  {value: 'STOCKS', label: 'Stocks'},
  {value: 'BONDS', label: 'Bonds'},
  {value: 'MUTUAL_FUNDS', label: 'Mutual Funds'},
  {value: 'OTHER', label: 'Other'},
];

const BASE_LANGUAGES = [
  {value: 'EN', label: 'English'},
  {value: 'FR', label: 'French'},
];

const getInitialValues = feedback => {
  const multilang = feedback?.multilang || [];
  const frLang = multilang.find(m => m?.language === 'FR');
  const enLang = multilang.find(m => m?.language === 'EN');

  let language = 'EN';
  let title = feedback?.title || '';
  let description = feedback?.description || '';
  let multilangTitle = '';
  let multilangDescription = '';

  if (frLang && (frLang.title || frLang.description)) {
    language = 'EN';
    multilangTitle = frLang.title || '';
    multilangDescription = frLang.description || '';
  } else if (enLang && (enLang.title || enLang.description)) {
    language = 'FR';
    multilangTitle = enLang.title || '';
    multilangDescription = enLang.description || '';
  }

  return {
    language,
    title,
    description,
    multilangTitle,
    multilangDescription,
    featureId: feedback?.featureId,
    isActive: feedback?.isActive ?? true,
    clickThreshold: feedback?.triggers?.clickThreshold ?? 0,
    timeOnFeatureThreshold: feedback?.triggers?.timeOnFeatureThreshold ?? 0,
    triggerInAppReviewAfterSubmit:
      feedback?.triggerInAppReviewAfterSubmit ?? false,
  };
};

export const UpdateMiniFeedback = ({feedback, closeModal, refetch}) => {
  const [updateMiniFeedback, {loading}] = useMutation(UPDATE_MINI_FEEDBACK);
  const [translateText] = useMutation(TRANSLATE_TEXT);
  const [translatingField, setTranslatingField] = useState(null);
  const [form] = Form.useForm();

  const initialValues = useMemo(
    () => getInitialValues(feedback),
    [feedback?.id],
  );

  const handleTranslate = async (field, sourceLang, targetLang) => {
    const sourceText = form.getFieldValue(field);
    if (!sourceText?.trim()) {
      toast.error(
        `Please enter ${field} in ${
          sourceLang === 'EN' ? 'English' : 'French'
        } first`,
      );
      return;
    }
    setTranslatingField(field);
    try {
      const {data} = await translateText({
        variables: {
          sourceLang,
          targetLang,
          sourceText,
        },
      });
      if (
        data?.translateText?.statusCode === 400 ||
        data?.translateText?.__typename === 'Error'
      ) {
        toast.error(data?.translateText?.message || 'Translation failed');
      } else {
        const translated = data?.translateText?.targetText || '';
        const targetField =
          field === 'title' ? 'multilangTitle' : 'multilangDescription';
        form.setFieldValue(targetField, translated);
        toast.success('Translated successfully');
      }
    } catch (err) {
      toast.error(err?.message || 'Translation failed');
    } finally {
      setTranslatingField(null);
    }
  };

  const onFinish = values => {
    const {
      language,
      title,
      description,
      multilangTitle,
      multilangDescription,
      featureId,
      isActive,
      clickThreshold,
      triggerInAppReviewAfterSubmit,
    } = values;

    const inputParams = {
      id: feedback.id,
      language: language || 'EN',
      title,
      description: description || undefined,
      featureId: featureId?.toUpperCase()?.replace(/ /g, '_'),
      isActive: !!isActive,
      triggerInAppReviewAfterSubmit: !!triggerInAppReviewAfterSubmit,
      triggers: {
        clickThreshold: clickThreshold ?? 0,
      },
    };

    const translationLang = language === 'EN' ? 'FR' : 'EN';
    if (multilangTitle?.trim() || multilangDescription?.trim()) {
      inputParams.multilang = [
        {
          language: translationLang,
          title: multilangTitle?.trim() || undefined,
          description: multilangDescription?.trim() || undefined,
        },
      ];
    }

    updateMiniFeedback({
      variables: {
        input: inputParams,
      },
    })
      .then(({data}) => {
        if (data?.updateMiniFeedback?.__typename === 'Error') {
          toast.error(data.updateMiniFeedback.message);
        } else {
          toast.success(
            data?.updateMiniFeedback?.message ||
              'Feature Feedback updated successfully',
          );
          form.resetFields();
          refetch();
          closeModal();
        }
      })
      .catch(err => {
        toast.error(err.message || 'An error occurred');
      });
  };

  const baseLang = Form.useWatch('language', form) ?? initialValues.language;
  const translationLabel = baseLang === 'EN' ? 'French' : 'English';
  const baseLabel = baseLang === 'EN' ? 'English' : 'French';
  const sourceLang = baseLang;
  const targetLang = baseLang === 'EN' ? 'FR' : 'EN';

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={initialValues}>
      <h1 className={styles.formTitle}>Update Feature Feedback</h1>

      <Form.Item
        name="language"
        label="Base Language"
        rules={[{required: true, message: 'Base language is required'}]}>
        <Select placeholder="Select base language">
          {BASE_LANGUAGES.map(lang => (
            <Select.Option key={lang.value} value={lang.value}>
              {lang.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="featureId"
        label="Feature"
        rules={[{required: true, message: 'Feature is required'}]}>
        <Select placeholder="Select a feature" showSearch>
          {FEATURE_IDS.map(feature => (
            <Select.Option key={feature.value} value={feature.value}>
              {feature.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <div className={styles.sectionCard}>
        <h3 className={styles.sectionHeading}>Title & Description</h3>

        <div className={styles.titleDescriptionGrid}>
          <div className={styles.gridCell}>
            <div className={styles.translateRow}>
              <Form.Item
                name="title"
                label={`Title (${baseLabel})`}
                rules={[{required: true, message: 'Title is required'}]}
                className={styles.translateRowFormItem}>
                <Input placeholder={`Enter title in ${baseLabel}`} />
              </Form.Item>
              <div className={styles.translateButtonWrap}>
                <button
                  type="button"
                  className={styles.translateText}
                  disabled={translatingField !== null}
                  onClick={() =>
                    handleTranslate('title', sourceLang, targetLang)
                  }>
                  {translatingField === 'title'
                    ? 'Translating...'
                    : 'Translate'}
                </button>
              </div>
            </div>
            <div className={styles.translateRow}>
              <Form.Item
                name="description"
                label={`Description (${baseLabel})`}
                rules={[
                  {
                    required: true,
                    message: `Description (${baseLabel}) is required`,
                  },
                ]}
                className={styles.translateRowFormItem}>
                <Input.TextArea
                  rows={3}
                  placeholder={`Enter description in ${baseLabel}`}
                />
              </Form.Item>
              <div className={styles.translateButtonWrap}>
                <button
                  type="button"
                  className={styles.translateText}
                  disabled={translatingField !== null}
                  onClick={() =>
                    handleTranslate('description', sourceLang, targetLang)
                  }>
                  {translatingField === 'description'
                    ? 'Translating...'
                    : 'Translate'}
                </button>
              </div>
            </div>
          </div>
          <div className={styles.gridCell}>
            <Form.Item
              name="multilangTitle"
              label={`Title (${translationLabel})`}
              rules={[
                {
                  required: true,
                  message: `Title (${translationLabel}) is required`,
                },
              ]}
              className={styles.formItemSpacing}>
              <Input
                placeholder={`Enter or translate to ${translationLabel}`}
              />
            </Form.Item>
            <Form.Item
              name="multilangDescription"
              label={`Description (${translationLabel})`}
              rules={[
                {
                  required: true,
                  message: `Description (${translationLabel}) is required`,
                },
              ]}
              className={styles.formItemSpacing}>
              <Input.TextArea
                rows={3}
                placeholder={`Enter or translate to ${translationLabel}`}
              />
            </Form.Item>
          </div>
        </div>
      </div>

      <Form.Item name="isActive" label="Is Active" valuePropName="checked">
        <Switch />
      </Form.Item>

      <h3 className={styles.triggersHeading}>Triggers</h3>
      <Form.Item
        name="triggerInAppReviewAfterSubmit"
        label="Trigger In-App Review After Submit"
        valuePropName="checked"
        tooltip="If enabled, users will be prompted for an in-app review after submitting feedback.">
        <Switch />
      </Form.Item>
      <Form.Item
        name="clickThreshold"
        tooltip="Number of times user have to click the feature to trigger the feedback"
        label="Click Threshold (Clicks)"
        rules={[
          {
            required: true,
            validator: (_, value) => {
              if (value === undefined || value === null || value === '') {
                return global.Promise.reject(
                  new Error('Click threshold is required'),
                );
              }
              return global.Promise.resolve();
            },
          },
        ]}>
        <InputNumber
          className={styles.inputFullWidth}
          min={0}
          placeholder="Number of clicks"
        />
      </Form.Item>

      <div className={styles.submitRow}>
        <SubmitButton label="Update" loading={loading} disabled={loading} />
      </div>
    </Form>
  );
};
